import React, { useState, useEffect, useContext } from 'react';
import RequestService from '../../services/RequestService';
import TableComponent from '../../widgets/TableComponent';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import {
    Grid2 as Grid,
    Typography,
    Breadcrumbs,
    Link,
    Stack,
    Chip,
    TextField,
    Button,
    Box,
    Snackbar,
    Alert,
    useMediaQuery,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import { UserContext } from '../../contexts/UserContext';
import { useTheme } from '@mui/material/styles';
import EditGroupDialog from '../../widgets/groups/EditGroupDialog';
import NewGroupDialog from '../../widgets/groups/NewGroupDialog';
import GroupList from '../../widgets/groups/GroupList';
import GroupDeleteDialog from '../../widgets/groups/GroupDeleteDialog';

const GroupsPage = () => {
    const { user, hasPermission } = useContext(UserContext);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selectedGroup, setSelectedGroup] = useState(null);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [searchTerm, setSearchTerm] = useState('');
    const [games, setGames] = useState([]);
    const [gameId, setGameId] = useState('');

    useEffect(() => {
        fetchGroups();
    }, []);

    const fetchGroups = async () => {
        try {
            setLoading(true);
            const data = await RequestService.getGroups(
                user.company.id,
                searchTerm,
                gameId
            );
            setGroups(Array.isArray(data) ? data : []);
        } catch (err) {
            setError('Erro ao carregar grupos.');
            setGroups([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchGames = async () => {
            try {
                const gameData = await RequestService.getGames(user.company.id);
                setGames(Array.isArray(gameData) ? gameData : []);
            } catch (err) {
                console.error('Erro ao carregar jogos:', err);
            }
        };

        fetchGames();
    }, [user.company.id]);

    const handleOpenAddDialog = () => setIsAddDialogOpen(true);
    const handleCloseAddDialog = () => setIsAddDialogOpen(false);

    const handleSuccessAdd = async () => {
        const data = await RequestService.getGroups(user.company.id);
        setGroups(data);
        setLoading(false);

        setSnackbarMessage('Usuário criado com sucesso!');
        setSnackbarOpen(true);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const handleOpenEditDialog = (group) => {
        if (hasPermission('GROUP_UPDATE')) {
            setSelectedGroup(group);
            setIsEditDialogOpen(true);
        }
    };

    const handleCloseEditDialog = () => {
        setIsEditDialogOpen(false);
        setSelectedGroup(null);
    };

    const handleOpenDeleteDialog = (group) => {
        setSelectedGroup(group);
        setIsDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setIsDeleteDialogOpen(false);
        setSelectedGroup(null);
    };

    const handleSuccessDelete = async () => {
        const data = await RequestService.getGroups(user.company.id);
        setGroups(data);
        setSnackbarMessage('Grupo excluído com sucesso!');
        setSnackbarOpen(true);
    };

    const handleSuccessEdit = async () => {
        const data = await RequestService.getGroups(user.company.id);
        setGroups(data);

        setSnackbarMessage('Grupo atualizado com sucesso!');
        setSnackbarOpen(true);
    };

    if (loading) return <div>Carregando...</div>;
    if (error) return <div>{error}</div>;

    const columns = [
        { label: 'Nome do Grupo', field: 'name' },
        {
            label: 'Qtd. de jogadores',
            field: 'users',
            align: 'center',
            renderCell: (value, row) => (
                <Typography>{row.users?.length || 0}</Typography>
            ),
        },
        { label: 'Jogo', field: 'gameName', align: 'center' },
        {
            label: '',
            field: 'actions',
            align: 'right',
            renderCell: (value, row, stopPropagation) => (
                <Stack direction="row" spacing={2}>
                    {hasPermission('GROUP_UPDATE') && (
                        <Link
                            component="button"
                            onClick={(event) => {
                                stopPropagation(event);
                                handleOpenEditDialog(row);
                            }}
                            underline="none"
                            className="action-link"
                        >
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                            >
                                <EditOutlinedIcon fontSize="small" />
                                <Typography>Editar</Typography>
                            </Stack>
                        </Link>
                    )}
                    {hasPermission('GROUP_DELETE') && (
                        <Link
                            component="button"
                            onClick={(event) => {
                                stopPropagation(event);
                                handleOpenDeleteDialog(row);
                            }}
                            underline="none"
                            className="action-link"
                            sx={{ color: 'red' }}
                        >
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                            >
                                <DeleteOutlinedIcon fontSize="small" />
                                <Typography>Excluir</Typography>
                            </Stack>
                        </Link>
                    )}
                </Stack>
            ),
        },
    ];

    const renderEmptyState = () => (
        <Box
            sx={{
                textAlign: 'center',
                mt: 4,
                color: '#666',
                fontFamily: 'Poppins',
                fontSize: 18,
            }}
        >
            <Typography variant="h6">Nenhum grupo cadastrado.</Typography>
            <Typography variant="body2">
                Clique em <strong>"Novo grupo"</strong> para adicionar o
                primeiro!
            </Typography>
        </Box>
    );

    const renderMobileState = () => {
        return groups.length === 0 ? (
            renderEmptyState()
        ) : (
            <GroupList
                users={groups}
                shouldEdit={hasPermission('GROUP_UPDATE')}
                shouldDelete={hasPermission('GROUP_DELETE')}
                onUserClick={handleOpenEditDialog}
                onDeleteClick={handleOpenDeleteDialog}
            />
        );
    };

    const renderDesktopLayout = () => {
        return groups.length === 0 ? (
            renderEmptyState()
        ) : (
            <Grid container justifyContent="center" xs={12}>
                <Box sx={{ width: '100%', overflowX: 'auto', mt: 2 }}>
                    <TableComponent columns={columns} rows={groups} />
                </Box>
            </Grid>
        );
    };

    return (
        <Box>
            <Grid container justifyContent="start">
                <Breadcrumbs aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" href="/">
                        Início
                    </Link>
                    <Link underline="hover" color="inherit">
                        Jogadores
                    </Link>
                    <Typography sx={{ color: 'text.primary' }}>
                        Grupos
                    </Typography>
                </Breadcrumbs>
            </Grid>

            <Grid container xs={12} sx={{ mt: 3 }}>
                <Typography
                    sx={{
                        fontFamily: 'Poppins',
                        fontSize: 24,
                        fontWeight: '500',
                        color: '#0C3466',
                        mb: 2,
                    }}
                >
                    Grupos
                </Typography>
            </Grid>

            {/* <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                    <TextField
                        id="search"
                        label="Pesquisar Nome"
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                fetchGroups();
                            }
                        }}
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12}>
                    <FormControl sx={{ minWidth: 200, width: '100%' }}>
                        <InputLabel>Jogo</InputLabel>
                        <Select
                            value={gameId}
                            onChange={(e) => setGameId(e.target.value)}
                            fullWidth
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {games.map((game) => (
                                <MenuItem key={game.id} value={game.id}>
                                    {game.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        sx={{ minHeight: '47px' }}
                        onClick={fetchGroups}
                        fullWidth
                    >
                        Aplicar Filtros
                    </Button>
                </Grid>
                <Grid item sx={12}>
                    <Button
                        variant="contained"
                        sx={{ minHeight: '47px' }}
                        endIcon={<AddCircleOutlineOutlinedIcon />}
                        onClick={handleOpenAddDialog}
                    >
                        Novo Grupo
                    </Button>
                </Grid>
            </Grid> */}

            <Grid
                container
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
                wrap="wrap"
            >
                {/* Left side: filters */}
                <Grid container item xs={12} md={9} spacing={2}>
                    {/* Search */}
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            id="search"
                            label="Pesquisar Nome"
                            variant="outlined"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    fetchGroups();
                                }
                            }}
                            fullWidth
                        />
                    </Grid>
                    {/* Grupo */}
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl sx={{ minWidth: 200, width: '100%' }}>
                            <InputLabel>Jogo</InputLabel>
                            <Select
                                value={gameId}
                                onChange={(e) => setGameId(e.target.value)}
                                fullWidth
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {games.map((game) => (
                                    <MenuItem key={game.id} value={game.id}>
                                        {game.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Aplicar Filtros */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            variant="contained"
                            sx={{ minHeight: '47px' }}
                            onClick={fetchGroups}
                            fullWidth
                        >
                            Aplicar Filtros
                        </Button>
                    </Grid>
                </Grid>

                {/* Right side: “Nova Missão” button */}
                <Grid item xs={12} md="auto">
                    {hasPermission('GROUP_CREATE') && (
                        <Button
                            variant="contained"
                            sx={{ minHeight: '47px' }}
                            endIcon={<AddCircleOutlineOutlinedIcon />}
                            onClick={handleOpenAddDialog}
                        >
                            Novo Grupo
                        </Button>
                    )}
                </Grid>
            </Grid>

            {isMobile ? renderMobileState() : renderDesktopLayout()}

            <EditGroupDialog
                open={isEditDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseEditDialog();
                    }
                }}
                user={user}
                group={selectedGroup}
                onSuccess={handleSuccessEdit}
            />

            <NewGroupDialog
                open={isAddDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseAddDialog();
                    }
                }}
                onSuccess={handleSuccessAdd}
                user={user}
            />

            <GroupDeleteDialog
                open={isDeleteDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseDeleteDialog();
                    }
                }}
                group={selectedGroup}
                onSuccess={handleSuccessDelete}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity="success"
                    variant="filled"
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default GroupsPage;
